import clsx from "clsx";
import { RefreshCw, X } from "lucide-react";
import { useEffect, useState } from "react";

import { motion, usePresence } from "../components/presence";
import { Button, IconButton } from "../components/ui";
import { t } from "../i18n/i18n";
import { leaveWith } from "../state/unsavedDrafts";
import { flushOrderQueues } from "../views/order/orderSaveQueue";
import { noticeShown, reloadSafely, startVersionWatch, useVersion } from "./versionCheck";
import { Waiting } from "../components/Waiting";

/** Avis « Nouvelle version de Generex disponible » (audit de persistance m14) : posé en haut de l'écran, au-dessus de
 *  l'accueil comme de l'espace de travail, il entre en glissé depuis le haut et reste jusqu'au choix de l'utilisateur
 *  (rien ne recharge la page à sa place). « Recharger » passe par la question commune s'il reste une saisie non
 *  enregistrée, puis envoie les saisies en cours de l'OPO ; les brouillons restent sur ce poste. « Plus tard »
 *  l'écarte jusqu'à la version suivante. Plan : z-65, au-dessus de l'accueil (z-60), sous l'aide en ligne (z-70). */
export function VersionNotice() {
  const shown = useVersion(noticeShown);
  const dismiss = useVersion((s) => s.dismiss);
  const { mounted, closing } = usePresence(shown);
  const [reloading, setReloading] = useState(false);

  useEffect(() => startVersionWatch(), []);

  if (!mounted) return null;
  // Une fiche ouverte non enregistrée (ordre de bataille, incident, 3A…) fait d'abord poser la question commune
  // « Abandonner les modifications ? » (enregistrer, abandonner ou revenir), plutôt que celle du navigateur.
  const reload = () =>
    leaveWith(() => {
      setReloading(true);
      void reloadSafely(
        () => flushOrderQueues(),
        () => window.location.reload(),
      );
    });

  return (
    <div className="pointer-events-none absolute inset-x-0 top-3 z-[65] flex justify-center px-3">
      <div
        role="status"
        aria-live="polite"
        className={clsx(
          "pointer-events-auto flex max-w-[560px] items-start gap-3 rounded-xl border border-line bg-surface py-2.5 pr-2 pl-3.5 shadow-float",
          motion("top", closing),
        )}
      >
        <RefreshCw size={16} className="mt-0.5 shrink-0 text-accent" />
        <div className="min-w-0 flex-1 py-px">
          <div className="text-ui leading-snug font-semibold break-words text-ink">{t("Nouvelle version de Generex disponible")}</div>
          <div className="mt-0.5 text-meta leading-snug break-words text-ink-muted">
            {t("Rechargez la page pour en profiter : vos saisies en cours sont enregistrées d'abord, vos brouillons restent sur ce poste.")}
          </div>
        </div>
        <Button size="sm" variant="primary" className="-my-0.5 shrink-0" onClick={reload} disabled={reloading}>
          {reloading ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Recharger")}
        </Button>
        <IconButton size="sm" onClick={dismiss} title={t("Plus tard")} aria-label={t("Plus tard")} className="-my-1">
          <X size={14} />
        </IconButton>
      </div>
    </div>
  );
}
